import { Injectable } from '@angular/core';

/**
 * Source: https://github.com/BCJTI/ng2-cookies
 * Release: v0.1.2
 */

/**
 * Class Cookie - Holds static functions to deal with Cookies
 */

@Injectable()
export class CookieService {
  /**
   * Checks the existence of a single cookie by it's name
   *
   * @param  {string} name Identification of the cookie
   * @returns existence of the cookie
   */
  public check(name: string): boolean {
    if (typeof document === 'undefined') return false; // Check if document exist avoiding issues on server side prerendering
    const _name = encodeURIComponent(name);
    const regexp = new RegExp('(?:^' + _name + '|;\\s*' + _name + ')=(.*?)(?:;|$)', 'g');
    const exists = regexp.test(document.cookie);
    return exists;
  }

  /**
   * Retrieves a single cookie by it's name
   *
   * @param  {string} name Identification of the Cookie
   * @returns The Cookie's value
   */
  public get(name: string): string {
    if (this.check(name)) {
      const _name = encodeURIComponent(name);
      const regexp = new RegExp('(?:^' + _name + '|;\\s*' + _name + ')=(.*?)(?:;|$)', 'g');
      const result = regexp.exec(document.cookie)!;
      return decodeURIComponent(result[1]);
    } else {
      return '';
    }
  }

  /**
   * Retrieves a a list of all cookie avaiable
   *
   * @returns Object with all Cookies
   */
  public getAll(): any {
    const cookies: any = {};

    // eslint-disable-next-line eqeqeq
    if (document.cookie && document.cookie != '') {
      const split = document.cookie.split(';');
      for (const s of split) {
        const currCookie = s.split('=');
        currCookie[0] = currCookie[0].replace(/^ /, '');
        cookies[decodeURIComponent(currCookie[0])] = decodeURIComponent(currCookie[1]);
      }
    }

    return cookies;
  }

  /**
   * Save the Cookie
   *
   * @param  {string} name Cookie's identification
   * @param  {string} value Cookie's value
   * @param  {number} expires Cookie's expiration date in days from now or at a specific date from a Date object. If it's undefined the cookie is a session Cookie
   * @param  {string} path Path relative to the domain where the cookie should be avaiable. Default /
   * @param  {string} domain Domain where the cookie should be avaiable. Default current domain
   * @param  {boolean} secure If true, the cookie will only be available through a secured connection
   */
  public set(
    name: string,
    value: string,
    expires?: number | Date,
    path?: string | null,
    domain?: string | null,
    secure?: boolean,
  ): void {
    let cookieStr = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';

    if (expires) {
      if (typeof expires === 'number') {
        const dtExpires = new Date(new Date().getTime() + expires * 1000 * 60 * 60 * 24);
        cookieStr += 'expires=' + dtExpires.toUTCString() + ';';
      } else {
        cookieStr += 'expires=' + expires.toUTCString() + ';';
      }
    }

    if (path) {
      cookieStr += 'path=' + path + ';';
    }
    if (domain) {
      cookieStr += 'domain=' + domain + ';';
    }
    if (secure) {
      cookieStr += 'secure;';
    }

    document.cookie = cookieStr;
  }

  /**
   * Removes specified Cookie
   *
   * @param  {string} name Cookie's identification
   * @param  {string} path Path relative to the domain where the cookie should be avaiable. Default /
   * @param  {string} domain Domain where the cookie should be avaiable. Default current domain
   */
  public delete(name: string, path?: string | null, domain?: string): void {
    this.set(name, '', -1, path, domain);
    const d = new Date();
    d.setTime(d.getTime() + -1 * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = name + '=;' + expires + ';path=/';
  }

  public doSaveInCookie(
    key: string,
    value: string,
    expiresDays?: number | null,
    secure: boolean = false,
    path: string | null = '/',
  ): void {
    this.set(key, value, expiresDays || 60, path, null, secure);
  }
}

export const Cookie = new CookieService();
